import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

import {
  ApiError,
  createApiClient,
  Forbidden,
  NotFound,
  Unauthenticated,
  Unavailable,
  Unprocessable,
} from "../api/client";
import type { AppConfig } from "../config";

/**
 * `notfound` and `unavailable` are SEPARATE variants on purpose.
 *
 * They mean opposite things — "we looked and there is nothing" versus "we could
 * not look" — and the API reference is explicit that a console rendering the
 * same empty state for both will tell an operator a merchant was deleted when
 * DynamoDB merely blipped. Keeping them apart in the type means no shared
 * handler can collapse them by accident.
 */
export type Resource<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "notfound"; error: ApiError }
  | { kind: "unavailable"; error: ApiError }
  | { kind: "denied"; error: ApiError }
  /**
   * 422 — the REQUEST was wrong, not the data.
   *
   * Reads could not be invalid until GET /admin/audit/{domain}, which 422s on
   * a domain outside tenant|quota|settings rather than returning an empty
   * list. That distinction is the whole point: an empty feed reads as "nothing
   * happened", and reporting silence where the server actually refused the
   * question is exactly the class of lie `notfound` vs `unavailable` exists to
   * prevent.
   */
  | { kind: "invalid"; error: ApiError }
  | { kind: "signedout" }
  | { kind: "error"; error: Error };

export interface UseResource<T> {
  state: Resource<T>;
  /** Re-fetch. Every write calls this rather than trusting its own response. */
  reload: () => void;
}

/** Map a thrown client error onto the Resource kind the pages already switch on. */
export function classifyError(err: unknown): Exclude<Resource<never>, { kind: "loading" | "ready" }> {
  if (err instanceof NotFound) return { kind: "notfound", error: err };
  if (err instanceof Unavailable) return { kind: "unavailable", error: err };
  if (err instanceof Forbidden) return { kind: "denied", error: err };
  if (err instanceof Unprocessable) return { kind: "invalid", error: err };
  if (err instanceof Unauthenticated) return { kind: "signedout" };
  if (err instanceof Error) return { kind: "error", error: err };
  return { kind: "error", error: new Error(String(err)) };
}

export function useResource<T>(
  config: AppConfig,
  path: string | null,
  deps: unknown[] = [],
): UseResource<T> {
  const auth = useAuth();
  const [state, setState] = useState<Resource<T>>({ kind: "loading" });
  const [nonce, setNonce] = useState(0);
  /** The path the current state belongs to, so a reload can be told from a move. */
  const loaded = useRef<string | null>(null);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const token = auth.user?.id_token;

  useEffect(() => {
    if (!path) return;

    // Guards against setting state after unmount, and against a slow response
    // from a previous path overwriting a newer one.
    let active = true;

    /**
     * A RELOAD KEEPS WHAT IS ON SCREEN. A move clears it.
     *
     * This used to set `loading` unconditionally, and every write calls
     * `reload()` — so a page that renders its editor only while `ready`
     * unmounted that editor on its own success, taking the "written, reaches
     * merchants in ~5 minutes" panel with it. The operator saw the
     * confirmation for about a frame and then a loading flash, which is
     * exactly the "conclude it failed and do it again" outcome the write
     * result exists to prevent. CapEditor and RemoveOverride both did this.
     *
     * Clearing on a PATH change still matters: without it, opening a second
     * merchant would show the first one's record under the new reference until
     * the response landed. `path` is the request URL, and every caller derives
     * it from the same values it passes as deps, so it is the honest key for
     * "is this the same read?".
     *
     * No new Resource variant, deliberately — `resourceStates.test.ts` requires
     * every page that handles one failure state to handle them all, so a
     * `revalidating` kind would ripple through a dozen pages to say something
     * no page needs to act on.
     */
    if (loaded.current !== path) setState({ kind: "loading" });

    const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });

    api
      .request<T>(path)
      .then((data) => {
        if (!active) return;
        loaded.current = path;
        setState({ kind: "ready", data });
      })
      .catch((err: unknown) => {
        if (!active) return;
        loaded.current = path;
        setState(classifyError(err));
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apiUrl, token, path, nonce, ...deps]);

  return { state, reload };
}
